import React, { useState, useEffect } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Grid,
  MenuItem,
  Button,
} from "@mui/material";
import { motion } from "framer-motion";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import config from "../../config";
import { useRoomNames } from './useRoomNames';
import { toast } from 'react-toastify';
export default function BookingReportPage() {
  const { roomLabel } = useRoomNames();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roomTypes, setRoomTypes] = useState([]);

  const [selectedRoomType, setSelectedRoomType] = useState("");
  const [selectedRoomNumber, setSelectedRoomNumber] = useState("");
  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const url = `${config.API_BASE_URL}${config.PRODUCT_LIST_API}`;
  const token = sessionStorage.getItem("token");
const company = JSON.parse(sessionStorage.getItem("companyDetails") || "{}");
const Companycurrency = company?.currency ?? "";
  const fetchBookings = async (roomType, roomNumber) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("roomType", roomType || "");
      params.append("roomNumber", roomNumber || "");
      params.append("packageId", 0);
      params.append("companyid", company?.id);

      const res = await fetch(
        `${config.API_BASE_URL}/home/Getallfilterbookings?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.ok) throw new Error("Failed to fetch bookings");
      const data = await res.json();
      setBookings(data || []);
    } catch (err) {
      console.error("Error fetching bookings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchRoomTypes = async () => {
      try {
        const res = await fetch(
          `${config.API_BASE_URL}/Home/GetAllRoomType/${encodeURIComponent(company?.id)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error("Failed to fetch room types");
        const data = await res.json();
        setRoomTypes(data || []);
      } catch (err) {
        console.error("Error fetching room types:", err);
      }
    };
    fetchRoomTypes();
  }, [token]);

  useEffect(() => {
    fetchBookings("", "");
  }, []);

  const handleFilterChange = () => {
    fetchBookings(selectedRoomType, selectedRoomNumber);
  };

  const filteredBookings = bookings.filter((entry) => {
    const matchesSearch = Object.values(entry).some((val) =>
      val?.toString().toLowerCase().includes(searchText.toLowerCase())
    );

    let matchesDate = true;
    if (fromDate) matchesDate = new Date(entry.checkIn) >= new Date(fromDate);
    if (toDate && matchesDate) matchesDate = new Date(entry.checkIn) <= new Date(toDate);

    return matchesSearch && matchesDate;
  });

  // 🔹 Export to Excel
  const exportToExcel = () => {
    if (!filteredBookings || filteredBookings.length === 0) {
      toast.error("No data to export!");
      return;
    }

    const formattedData = filteredBookings.map((entry, index) => ({
      "#": index + 1,
      "Booking ID": entry.bookingId,
      "Guest Name": `${entry.firstName} ${entry.lastName}`,
      "Hotel Name": entry.hotelName,
      "Room Type": entry.roomType,
      "Room Numbers": entry.roomNumbers,
      "Check-In": entry.isCancel ? "Cancelled" : new Date(entry.checkIn).toLocaleDateString('en-GB'),
      "Check-Out": entry.isCancel ? "Cancelled" : new Date(entry.checkOut).toLocaleDateString('en-GB'),
       [`Total Price (${Companycurrency})`]: entry.totelPrice?.toFixed(2),
      [`Pending Amount (${Companycurrency})`]: entry.isCancel ? "0.00" : entry.pendingAmount?.toFixed(2),
      Status: entry.isCancel ? "Cancelled" : "Active",
      "Cancel Remark": entry.isCancel ? entry.cancelRemark || "-" : "-",
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "BookingReport");

    XLSX.writeFile(workbook, "BookingReport.xlsx");
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="70vh">
        <CircularProgress size={60} />
      </Box>
    );

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" fontWeight="bold" mb={3} sx={{ color: "#0a2540", textAlign: "center" }}>
        🏨 Booking Report
      </Typography>

      {/* Filters */}
      <Grid container spacing={2} mb={3} justifyContent="center">
        <Grid item>
          <TextField label="🔍 Search" size="small" value={searchText} onChange={(e) => setSearchText(e.target.value)} sx={{ width: 250 }} />
        </Grid>
        <Grid item>
          <TextField select label="Room Type" size="small" value={selectedRoomType} onChange={(e) => setSelectedRoomType(e.target.value)} sx={{ width: 200 }}>
            <MenuItem value="">All</MenuItem>
            {roomTypes.map((r) => (
              <MenuItem key={r.id} value={r.roomtype}>
                {r.roomtype}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item>
          <TextField label="Room Number" size="small" value={selectedRoomNumber} onChange={(e) => setSelectedRoomNumber(e.target.value)} sx={{ width: 150 }} />
        </Grid>
        <Grid item>
          <TextField type="date" label="From Date" size="small" value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
        </Grid>
        <Grid item>
          <TextField type="date" label="To Date" size="small" value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 150 }} />
        </Grid>
        <Grid item>
          <Button variant="contained" onClick={handleFilterChange}>Apply Filter</Button>
        </Grid>

        {/* 🔹 Export Button */}
        <Grid item>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={exportToExcel}
            sx={{ borderRadius: 2, fontWeight: "bold" }}
          >
            Export
          </Button>
        </Grid>
      </Grid>

      {/* Table */}
      <Paper sx={{ borderRadius: 3, overflow: "hidden" }}>
        <TableContainer>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {[
                  "#",
                  "Booking ID",
                  "Guest Name",
                  "Hotel Name",
                  "Room Type",
                  "Room Numbers",
                  "Check-In",
                  "Check-Out",
                  `Total Price (${Companycurrency})`,
                  `Pending Amount (${Companycurrency})`,
                  "Status",
                  "Cancel Remark",
                ].map((head, index) => (
                  <TableCell key={index}>{head}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBookings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">No records found</TableCell>
                </TableRow>
              ) : (
                filteredBookings.map((entry, i) => {
                  const isCancelled = entry.isCancel;
                  return (
                    <TableRow key={i}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{entry.bookingId}</TableCell>
                      <TableCell>{`${entry.firstName} ${entry.lastName}`}</TableCell>
                      <TableCell>{entry.hotelName}</TableCell>
                      <TableCell>{entry.roomType}</TableCell>
                      <TableCell>{entry.roomNumbers ? String(entry.roomNumbers).split(',').map((n) => roomLabel(n.trim())).join(', ') : entry.roomNumbers}</TableCell>
                      <TableCell>{isCancelled ? "Cancelled" : new Date(entry.checkIn).toLocaleDateString('en-GB')}</TableCell>
                      <TableCell>{isCancelled ? "Cancelled" : new Date(entry.checkOut).toLocaleDateString('en-GB')}</TableCell>
                      <TableCell>{Companycurrency}{entry.totelPrice?.toFixed(2)}</TableCell>
                      <TableCell>{Companycurrency}{isCancelled ? "0.00" : entry.pendingAmount?.toFixed(2)}</TableCell>
                      <TableCell sx={{ color: isCancelled ? "red" : "green", fontWeight: "bold" }}>
                        {isCancelled ? "Cancelled" : "Active"}
                      </TableCell>
                      <TableCell>{isCancelled ? entry.cancelRemark || "-" : "-"}</TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
